import React, { useEffect, useRef, useState } from "react";
import ImageStarContainer from "../Levels/ImageStarContainer/ImageStarContainer";
import { getLevel } from "../../api";
import pushHistory from "../../functions/pushHistory";
import { useHistory } from "react-router-dom";
import UsernameAvatarDisplay from "./UsernameAvatarDisplay";
import LevelInfo from "../Levels/LevelInfo";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

const LinkRenderer = (props) => {
	const [useImageEmbed, setUseImageEmbed] = useState(false);
	const [useVideoEmbed, setUseVideoEmbed] = useState(false);
	const [useRawText, setUseRawText] = useState(false);
	const imageRef = useRef<null | HTMLImageElement>(null);
	const iframeSrc = useRef<string | null>(null);

	const decoratedHref = props.decoratedHref;
	const setHasEmbed = props?.setHasEmbed;
	const setLevelEmbeds = props?.setLevelEmbeds;

	const authorRank: number | string = props?.rank;
	const authorRankNum: number =
		typeof authorRank === "string" ? parseInt(authorRank, 10) : authorRank;

	const [levelId, setLevelId] = useState("");
	const history = useHistory();

	const extractYouTubeVideoInfo = (url) => {
		const youtubeRegex =
			/(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)(?:.*?[?&]t=([0-9]+))?/;
		const match = url.match(youtubeRegex);
		if (!match) return [null, null];

		const videoID = match[1];
		const startTime = match[2] ? parseInt(match[2], 10) : null;

		return [videoID, startTime];
	};

	useEffect(() => {
		// prevent linkification if nolink=1
		if (
			decoratedHref?.includes("&unlink=1") ||
			decoratedHref?.includes("?unlink=1")
		)
			return setUseRawText(true);

		// skip embedding if embed=0
		if (
			decoratedHref?.includes("&embed=0") ||
			decoratedHref?.includes("?embed=0")
		)
			return;

		// embedding levels works for any rank
		const isLevelLink = decoratedHref?.match(
			/levelsharesquare\.com\/levels\/[a-f\d]{24}/i,
		);
		if (
			isLevelLink &&
			!levelId &&
			setLevelEmbeds &&
			props?.levelEmbeds?.length < 3
		) {
			const currentId = isLevelLink[0].split("/").pop();
			setLevelId(currentId);
			// fetch the level by id
			let success = false;
			getLevel({ id: currentId }).then((response) => {
				if (response?.data?.level) {
					// upon success set the level
					setLevelEmbeds((prev) => [
						...(prev || []),
						response?.data?.level,
					]);
					if (setHasEmbed) setHasEmbed(true);
					success = true;
				}
			});

			// end the function if it wasnt a valid level
			if (success) return;
		}

		// dont embed a link
		if (props?.levelEmbeds && isLevelLink) return;

		// only embed if the author is rank 8 or above
		if (authorRankNum < 8 && authorRank !== "bypass") return;
		const image = new Image();

		// Handle image embedding
		image.src = decoratedHref;
		image.onload = () => {
			setUseImageEmbed(true);
			if (!imageRef.current) imageRef.current = image;
			if (setHasEmbed) setHasEmbed(true);
		};
		image.onerror = () => {
			setUseImageEmbed(false);
		};

		// Youtube video embeds
		if (
			decoratedHref.includes("youtube.com") ||
			decoratedHref.includes("youtu.be")
		) {
			const [videoID, start] = extractYouTubeVideoInfo(decoratedHref);
			if (videoID && !iframeSrc.current) {
				const appendString = start ? `?start=${start}` : "";
				iframeSrc.current = `https://www.youtube.com/embed/${videoID}${appendString}`;
				setUseVideoEmbed(true);
				if (setHasEmbed) setHasEmbed(true);
			}
			return;
		}

		// vimeo embeds
		if (decoratedHref.includes("vimeo.com") && !iframeSrc.current) {
			iframeSrc.current = decoratedHref.replace(
				"vimeo.com",
				"player.vimeo.com/video",
			);
			if (setHasEmbed) setHasEmbed(true);
			return setUseVideoEmbed(true);
		}
		// streamable embeds
		if (decoratedHref.includes("streamable.com") && !iframeSrc.current) {
			iframeSrc.current = decoratedHref.replace(
				"streamable.com",
				"streamable.com/e",
			);
			if (setHasEmbed) setHasEmbed(true);
			return setUseVideoEmbed(true);
		}

		return () => {
			setUseImageEmbed(false);
			setUseVideoEmbed(false);
			imageRef.current = null;
			iframeSrc.current = null;
		};
	}, [
		decoratedHref,
		setHasEmbed,
		authorRank,
		authorRankNum,
		setLevelEmbeds,
		levelId,
		props?.levelEmbeds,
	]);

	// don't turn into a link if unlink=1
	if (useRawText) {
		let splitSym = "";
		// loop through the decorated text
		for (let i = 0; i < props.decoratedText.length; i++) {
			if (["&", "?"].includes(props.decoratedText[i])) {
				splitSym = props.decoratedText[i];
				break;
			}
		}
		// split
		const rawText = props.decoratedText.split(splitSym);
		return <span>{rawText[0]}</span>;
	}

	// embed a level
	const index = props?.levelEmbeds?.findIndex((lvl) => lvl?._id === levelId);
	if (props?.levelEmbeds?.length && index !== -1) {
		const level = props?.levelEmbeds?.[index];
		return <LevelEmbed level={level} history={history} newTab={false} />;
	}

	// embed an image
	if (useImageEmbed && imageRef.current)
		return (
			<img
				src={imageRef?.current?.src}
				alt={props.decoratedText}
				className="linkify-embed"
			/>
		);

	// embed a youtube video
	if (useVideoEmbed && iframeSrc.current)
		return (
			<iframe
				src={iframeSrc.current}
				title={props.decoratedText}
				className="linkify-video"
			/>
		);
	// default behaviour for links
	if (
		decoratedHref !== undefined &&
		(decoratedHref.includes("&text=") || decoratedHref.includes("?text="))
	) {
		const url = new URL(decoratedHref);
		const text = url.searchParams.get("text");
		url.searchParams.delete("text");

		return (
			<OverlayTrigger
				placement="top"
				overlay={
					<Tooltip id="danger-link">
						<span className="danger-link">{url.toString()}</span>
					</Tooltip>
				}>
				<a
					href={url.toString()}
					className="danger"
					target="_blank"
					rel="noopener noreferrer">
					{text || props.decoratedText}
				</a>
			</OverlayTrigger>
		);
	}

	return (
		<a
			href={decoratedHref}
			target="_blank" // open links in a new tab
			rel="noopener noreferrer">
			{props.decoratedText}
		</a>
	);
};

export const LevelEmbed = ({ level, history, newTab, href = "" }) => {
	const levelName =
		level?.name?.length > 32
			? `${level?.name?.slice(0, 32)}...`
			: level?.name;

	return (
		<div
			className="linkify-level"
			onClick={() =>
				pushHistory(
					history,
					href ? href : `/levels/${level?._id}`,
					newTab,
				)
			}>
			<div style={{ width: "max-content" }}>
				<div>
					<a href={`#!`}>{levelName}</a>
				</div>
				<ImageStarContainer level={level} />
			</div>
			<div>
				<div className="d-none d-lg-block">
					<UsernameAvatarDisplay
						user={level?.author}
						setCardPosition={null}
						setShowCard={null}
						setHoverCardUser={null}
						showCard={null}
						newTab={newTab}
					/>
				</div>
				<LevelInfo level={level} />
			</div>
		</div>
	);
};

export default LinkRenderer;
